import React, { Component } from 'react';
import { Route } from 'react-router';
import * as Colyseus from "@colyseus/sdk";
import QRCode from 'qrcode.react';
import { Howl, Howler } from "howler";

import { audio, vo, sfx, music, wait } from 'services/AudioManager';
import { createSeededRandoms } from 'utils/seededRandom';
import { MUSIC, VO, SFX } from "constants/audio";

import { APP_TYPES } from 'constants/AppTypes';
import OptionsMenu from "components/Shared/OptionsMenu/OptionsMenu";
import ConnectingOverlay from "components/Shared/ConnectingOverlay/ConnectingOverlay";

import "animate.css";
import styles from 'components/Host/HostStyles.module.scss';

import VideoPlayer from 'components/Host/VideoPlayer/VideoPlayer';
import Countdown from 'components/Host/Countdown';
import RoomDetails from 'components/Host/RoomDetails/RoomDetails';
import ConsoleOverlayPanel from 'components/Shared/ConsoleOverlayPanel/ConsoleOverlayPanel';
import PerfOverlay, { PERF_OVERLAY, tvOverlayScale } from 'components/Shared/PerfOverlay/PerfOverlay';
import AudioDebugOverlay, { AUDIO_DEBUG } from 'components/Shared/AudioDebugOverlay/AudioDebugOverlay';


import { testRoomState } from "constants/testData";
import { GAME_STATES, GAME_ID, MESSAGES, QuestionTypes, IMAGES, VIDEOS, VIDEO_EVENTS } from "constants/constants";
import { retryDelayForCode, isTerminalReconnectError } from "services/connectionManager";

const fullscreenAvailable = document.fullscreenEnabled || document.mozFullscreenEnabled || document.webkitFullscreenEnabled ? true : false;
const useTestState = false;
const optimised = true;
const showConsolePanel = false;

// Names on the podium stand screen (renderTeam: MENU / GAME_OVER / END_GAME).
// One or two names stack as a centred block, both at one size. The size is
// worked out from the text (no DOM measuring), shrinking only when the widest
// name would run past STAND_NAME_MAX_WIDTH_CH. Line spacing lives in
// HostStyles.module.scss ($stand-name-line-height).
const STAND_NAME_FONT_CH = 2.16;      // base size; keep in step with .score.small
const STAND_NAME_MAX_WIDTH_CH = 16;   // widest line allowed (screen inside the bezel is ~18.6ch)
// Avant Bold advance widths in em, measured from ITC-AVANT-GARDE-GOTHIC-BOLD.OTF.
// Nicknames are upper-case A-Z / 0-9 / space / _ (GameCityUX Join.js).
const AVANT_WIDTH_EM = {
    A: 0.76, B: 0.65, C: 0.83, D: 0.74, E: 0.53, F: 0.51, G: 0.85, H: 0.75, I: 0.32,
    J: 0.58, K: 0.71, L: 0.49, M: 0.92, N: 0.76, O: 0.85, P: 0.64, Q: 0.87, R: 0.65,
    S: 0.57, T: 0.5, U: 0.7, V: 0.72, W: 0.98, X: 0.76, Y: 0.65, Z: 0.5, " ": 0.31, _: 0.5,
};
const AVANT_DIGIT_EM = 0.62;
const AVANT_OTHER_EM = 0.98; // anything unexpected: assume the widest letter

const avantWidthEm = (text) => [...String(text ?? "").toUpperCase()].reduce(
    (w, c) => w + (AVANT_WIDTH_EM[c] ?? (c >= "0" && c <= "9" ? AVANT_DIGIT_EM : AVANT_OTHER_EM)), 0);

const stateOptions = Object.entries(GAME_STATES).filter(
    ([k]) => typeof k === "string" && isNaN(+k)
);

export class Host extends Component {
    static displayName = Host.name;

    constructor(props) {
        super(props);

        this.client = new Colyseus.Client(process.env.REACT_APP_GAME_SERVER_URL);
        this.state = {
            roomId: 0,
            room: null,
            myId: null,
            roomState: useTestState ? testRoomState : {},
            redirect: null,
            reconnectTries: 0,
            connected: useTestState ? true : false,
            muted: false,
            menuOpen: false,
            hostLoadedSent: false,
            isMirror: false,

            countdownValue: 100,
            countdownFinished: false,

            players: [],
            //gameState: GAME_STATES.REVEAL_ANSWER, // debug
            gameState: "",

            nextGameState: "",
            nextStill: null,
            nextStillStyles: "",

            playingVideo: false,
            videoSrc: null,
            shouldPlayVideo: true,
            activeStillBG: "",
            activeStillStyles: "",
            hideVideoOverlays: false,
            videoEvents: [],
            revealPodiumsOnVideo: false,
            fadeCountdownTower: false,
        };

        this.toggleMute = this.toggleMute.bind(this);
        this.toggleMenu = this.toggleMenu.bind(this);
        this.videoPlayerRef = React.createRef();
        this.handleVideoError = this.handleVideoError.bind(this);
    }

    sleep(ms) {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }

    componentDidUpdate(prevProps, prevState) {
        // Check if we should send HOST_LOADED when loading completes or connection is established
        if ((prevProps.isLoadingComplete !== this.props.isLoadingComplete ||
            prevState.connected !== this.state.connected) &&
            !prevState.hostLoadedSent) {
            this.sendHostLoadedIfReady();
        }

        // Release the frozen question snapshot once the outgoing pills have
        // finished transitioning away (1s + 0.2s delay in HostStyles). Rounds
        // alternate SINGLE/GROUP, and each type renders a different element
        // tree — so if the snapshot were held until showQuestion flips back to
        // true, React would mount the new round's pills in the same commit as
        // `.show` and they'd paint already un-rotated, skipping the flip-in.
        // Releasing early lets them mount in their hidden pose ahead of time.
        if (prevState.showQuestion && !this.state.showQuestion) {
            clearTimeout(this._snapshotReleaseTimer);
            this._snapshotReleaseTimer = setTimeout(() => {
                if (this.state.showQuestion) return;
                this._questionViewSnapshot = null;
                this.forceUpdate();
            }, 1500);
        } else if (!prevState.showQuestion && this.state.showQuestion) {
            clearTimeout(this._snapshotReleaseTimer);
        }
    }

    sendHostLoadedIfReady = () => {
        const { isLoadingComplete } = this.props;
        const { connected, hostLoadedSent, room, isMirror } = this.state;

        if (isMirror) return;

        // Only send if both conditions are met, we haven't sent it yet, and we have a room state
        if (connected && isLoadingComplete && !hostLoadedSent && room) {
            console.log('Sending HOST_LOADED message');
            room.send(MESSAGES.TO_SERVER.HOST_LOADED, { gameId: GAME_ID });
            this.setState({ hostLoadedSent: true });
        }
    };

    async componentDidMount() {
        const mask = this.supportsMaskImage();
        //const slow = await this.isDeviceSlow();
        console.log("Supports Mask : ", mask);
        //console.log("Is Slow : ", slow);
        this.setState({
            //useMask: mask/* && !slow*/,
            useMask: false, // debug
        });

        this.doReconnect();

        document.addEventListener('visibilitychange', this.handleVisibilityChange);
        window.addEventListener('resize', this.handleResize);
        //this.toggleMute(true, false);

        // load first 2 videos
        this.loadVideo(VIDEOS.INTRO.src);
        this.loadVideo(VIDEOS.TRANSITION_MENU_R1.src);
    }


    componentWillUnmount() {
        document.removeEventListener('visibilitychange',
            this.handleVisibilityChange);
        window.removeEventListener('resize', this.handleResize);
        clearTimeout(this._resizeTimer);
        clearTimeout(this._snapshotReleaseTimer);
        Howler.stop();
    }

    handleVisibilityChange = () => {
        if (document.hidden && Howler) {
            Howler.mute(true);
        } else if (!document.hidden && Howler) {
            Howler.mute(this.state.muted);
        }
    }

    // Everything inside .gameContainer is sized off --ch (a proportion of the
    // 16:9 container). Several elements also carry transitions for their in-game
    // animations (podium raise, team slide/hide). Those transitions would
    // otherwise animate the size recalculation on every window resize, making
    // elements "float and catch up". Suppress transitions for the duration of an
    // active resize so sizes track the container instantly; the flag clears
    // ~200ms after resizing stops, restoring the gameplay animations. (Real TVs
    // never resize, so this only affects dev/preview.)
    handleResize = () => {
        const el = document.getElementById('gameContainer');
        if (!el) return;
        el.classList.add(styles.noTransition);
        clearTimeout(this._resizeTimer);
        this._resizeTimer = setTimeout(() => {
            el.classList.remove(styles.noTransition);
        }, 200);
    }

    loadVideo = (src) =>
        new Promise(res => {
            const video = document.createElement("video");
            video.preload = "metadata"; // or "auto" if you want full preload
            video.onloadedmetadata = () => {
                res();
                console.log("Video Loaded : ", src);
            };
            video.onerror = () => {
                res();
            };
            video.src = src;
            // For some browsers, load() must be called explicitly
            video.load();
        });

    supportsMaskImage() {
        return CSS.supports("mask-image", "linear-gradient(black, transparent)");
    }

    measureFPS(duration = 300) {
        return new Promise(resolve => {
            let frames = 0;
            let start = performance.now();

            function loop() {
                frames++;
                const now = performance.now();
                if (now - start >= duration) {
                    const fps = (frames / (now - start)) * 1000;
                    resolve(fps);
                } else {
                    requestAnimationFrame(loop);
                }
            }

            requestAnimationFrame(loop);
        });
    }

    async isDeviceSlow() {
        const fps = await this.measureFPS();
        // You can tweak the threshold.
        // Under 40 FPS = poor animation performance
        console.log("FPS : ", fps)
        return fps < 10;
    }

    toggleFullScreen() {
        if (fullscreenAvailable) {
            if (!document.fullscreenElement && !document.webkitIsFullScreen && !document.mozFullScreen && !document.msFullscreenElement) {
                let elem = document.documentElement
                if (elem.requestFullscreen) {
                    elem.requestFullscreen();
                } else if (elem.webkitRequestFullscreen) {
                    elem.webkitRequestFullscreen();
                } else if (elem.mozRequestFullScreen) {
                    elem.mozRequestFullScreen();
                } else if (elem.msRequestFullscreen) {
                    elem.msRequestFullscreen();
                }
            } else {
                if (document.exitFullscreen) {
                    document.exitFullscreen();
                } else if (document.webkitExitFullscreen) {
                    document.webkitExitFullscreen();
                } else if (document.mozExitFullscreen) {
                    document.mozExitFullscreen();
                } else if (document.msExitFullscreen) {
                    document.msExitFullscreen();
                }
            }
        }
    }

    getQueryStringValue(key) {
        return decodeURIComponent(window.location.search.replace(new RegExp("^(?:.*[&\\?]" + encodeURIComponent(key).replace(/[.+*]/g, "\\$&") + "(?:\\=([^&]*))?)?.*$", "i"), "$1"));
    }


    animatePotato(id, animation) {
        const elements = document.querySelectorAll(`[data-avatar="${id}"]`);
        elements.forEach(el => this.animateCSS(el, animation));
    }

    animateCSS = (element, animation, prefix = 'animate__') =>
        // We create a Promise and return it
        new Promise((resolve, reject) => {
            const animationName = `${prefix}${animation}`;
            let node;
            if (typeof element === `string`) {
                node = document.querySelector(element);
            } else {
                node = element;
            }
            node.classList.add(`${prefix}animated`, animationName);

            // When the animation ends, we clean the classes and resolve the Promise
            function handleAnimationEnd(event) {
                event.stopPropagation();
                node.classList.remove(`${prefix}animated`, animationName);
                resolve('Animation ended');
            }

            node.addEventListener('animationend', handleAnimationEnd, { once: true });
        });

    toggleMenu() {
        this.setState(s => ({ menuOpen: !s.menuOpen }));
    }

    sendIfPrimary = (message, data = {}) => {
        if (this.state.isMirror) return;
        this.state.room?.send(message, data);
    }

    get isPrimary() {
        return !this.state.isMirror;
    }

    toggleMute() {
        if (this.state.isMirror) return;
        const pointlessData = this.state.roomState?.pointlessData;
        if (!pointlessData || !this.state.room) return;
        const muted = !!pointlessData.mutedMusic && !!pointlessData.mutedSfx;
        this.state.room.send(MESSAGES.TO_SERVER.MUTE_MUSIC, { mute: !muted });
        this.state.room.send(MESSAGES.TO_SERVER.MUTE_SFX, { mute: !muted });
    }

    checkAndAddPlayer(player) {
        if (!this.state.players.find(elem => elem.id === player.id)) {
            this.setState((prevState) => {
                return { players: [...prevState.players, player] }
            });
            setTimeout(() => { // animate in timeout
                let statePlayers = [...this.state.players];
                let pos = statePlayers.map(function (e) { return e.id; }).indexOf(player.id);
                let statePlayer = { ...statePlayers[pos] };
                statePlayer.showPlayer = true;
                statePlayers[pos] = statePlayer;
                this.setState({ players: statePlayers });
            }, 500);
        }
    }

    removePlayer(id) {
        this.setState((prevState) => {
            return { players: prevState.players.filter(x => x.id !== id), }
        });
    }

    getRandomElement(array) {
        return array[Math.random() * array.length >> 0];
    }

    signalStartGame = () => {
        this.sendIfPrimary(MESSAGES.TO_SERVER.START_GAME, {});
    }
    goToLobby = () => {
        this.sendIfPrimary("change_game", {});
    }
    signalNewGame = () => {
        this.sendIfPrimary(MESSAGES.TO_SERVER.NEW_GAME, {});
    }

    getRedirectURL(display = false) {
        let url = display ? process.env.REACT_APP_GAME_CITY_URL_DISPLAY : process.env.REACT_APP_GAME_CITY_URL;

        return url;
    }

    updateToken(token) {
        var url = new URL(window.location.href);

        try {
            window.history.replaceState(null, null, (url.pathname) + (`?token=${token}`));
        } catch (e) {
            console.warn(e)
        }
    }

    //setupStateListeners = (roomState, $) => {
    //    $(roomState).players.onAdd((player, key) => {
    //        console.log(player, "has been added at", key);
    //        this.checkAndAddPlayer(player);

    //        const changes = ["connected", "votedSkip", "avatar", "name", "id", "primaryPlayer"];
    //        changes.forEach(change => {
    //            $(player).listen(change, (value) => {
    //                console.log(`Player ${player.name} Change ${change} to Value ${value}`);
    //                let statePlayers = [...this.state.players];
    //                let pos = statePlayers.map(function (e) { return e.id; }).indexOf(player.id);
    //                let statePlayer = { ...statePlayers[pos] };

    //                statePlayer[change] = value;

    //                statePlayers[pos] = statePlayer;
    //                this.setState({ players: statePlayers });
    //            });
    //        });

    //        /* Example player schema syncing: */
    //        //const ycsuPlayerChanges = ["teamIndex", "isDescriber", "isDinger", "ready", "currentRuleVoteId",];
    //        //ycsuPlayerChanges.forEach(change => {
    //        //    $(player).ycsuData.listen(change, (value) => {
    //        //        let statePlayers = [...this.state.players];
    //        //        let pos = statePlayers.map(function (e) { return e.id; }).indexOf(player.id);
    //        //        let statePlayer = { ...statePlayers[pos] };

    //        //        statePlayer.ycsuData[change] = value;

    //        //        statePlayers[pos] = statePlayer;
    //        //        this.setState({ players: statePlayers });
    //        //    });
    //        //});
    //    });

    //    /* Example game state listeners */
    //    //const ycsuStateVars = ["gameState", "roundNumber", "newRuleTeamId", "timer", "timerRunning", "teamSelectionType", "describingTeamId", "currentRuleBreaks", "currentValidationNum", "pointsToWin", "dangerZoneStart"];
    //    //ycsuStateVars.forEach((stateVar) => {
    //    //    $(roomState).ycsuData.listen(stateVar, (change) => {
    //    //        let update = {};
    //    //        update[stateVar] = change;
    //    //        this.setState(update);
    //    //    });
    //    //});
    //    //const ycsuStateArrayChanges = ["currentCorrectPairIds", "newRuleZones"];
    //    //ycsuStateArrayChanges.forEach(arrayName => {
    //    //    let update = {};
    //    //    update[arrayName] = [];
    //    //    this.setState(update);
    //    //    $(roomState).ycsuData[arrayName].onAdd((change, index) => {
    //    //        console.log(`YCSU Array ${arrayName} Change ${change} at index ${index}`);
    //    //        let array = [...this.state[arrayName]];
    //    //        array[index] = change;
    //    //        let changeUpdate = {};
    //    //        changeUpdate[arrayName] = array;
    //    //        this.setState(changeUpdate);
    //    //    });
    //    //    $(roomState).ycsuData[arrayName].onRemove((change, index) => {
    //    //        console.log(`YCSU Array ${arrayName} Removed ${change} at index ${index}`);
    //    //        let array = [...this.state[arrayName]];
    //    //        array[index] = null;
    //    //        let changeUpdate = {};
    //    //        changeUpdate[arrayName] = array;
    //    //        this.setState(changeUpdate);
    //    //    });
    //    //});
    //}

    async doWelcome() {
        audio.playMusic(MUSIC.PreCreditsTheme, { volume: 0.1, loop: true });
        await this.sleep(1000);
        await audio.playVo(VO.Welcome, {});
        //await audio.playVo(VO.WithMeRichard, {});
    }

    async doIntroFlow() {
        audio.stopAllAudio();
        audio.playMusic(MUSIC.TensionBed, { loop: true, volume: 0.1 });
        audio.playSfx(SFX.DisplaySweep);
        this.setState({ showRemaining: false, showCategory: false, showQuestion: false, countdownValue: 100, wrongAnswer: false, countdownFinished: false, showRoundNumber: true, })
        if (this.state.roomState?.pointlessData?.roundNumber === 0) {
            await audio.playVo(VO.AimOfGame, {});
            await this.sleep(500);
            await audio.playVo(VO.Podiums.FirstQuestion, {});
        }
        this.sendIfPrimary(MESSAGES.TO_SERVER.BEGIN_ROUND, {});
    }

    getPodiumsTurnVO() {
        switch (this.state.roomState?.pointlessData?.activeTeamIndex) {
            case 0:
                return VO.Podiums.Turn.Podium1;
            case 1:
                return VO.Podiums.Turn.Podium2;
            case 2:
                return VO.Podiums.Turn.Podium3;
            case 3:
                return VO.Podiums.Turn.Podium4;
            default:
                return VO.Podiums.Turn.Podium1;
        }
    }

    async doCategoryFlow() {
        const questionType = this.state.roomState?.pointlessData?.questionType;

        this.setState({ showCategory: false, showQuestion: false, showRemaining: false, hideRemaining: false, showDetailedQuestion: false, });
        audio.playMusic(MUSIC.TensionBed, { loop: true, volume: 0.1 });
        await audio.playSfx(VO.NextCategory[this.state.roomState?.pointlessData?.roundNumber - 1]);
        await this.sleep(750);
        this.setState({ showCategory: true, })
        audio.playSfx(SFX.DisplaySweep, {});
        await this.sleep(1250);
        await audio.playVo(VO.CategoryComment, {});
        await this.sleep(750);
        await audio.playVo(questionType === QuestionTypes.SINGLE ? VO.NextQuestionSingle : VO.NextQuestionGroup);
        await this.sleep(500);
        audio.playSfx(SFX.DisplaySweep, {});
        this.setState({ showDetailedQuestion: true });
        //this.setState({ showQuestion: true, });
        //await this.sleep(750);
        //await audio.playVo(this.getPodiumsTurnVO());
        //await this.sleep(500)
        this.sendIfPrimary(MESSAGES.TO_SERVER.START_PRE_ROUND, {});
    }

    async doQuestionOnlyFlow() {
        const questionType = this.state.roomState?.pointlessData?.questionType;

        this.setState({ showCategory: false, countdownValue: 100, wrongAnswer: false, countdownFinished: false, showRoundNumber: false, });
        audio.playMusic(MUSIC.TensionBed, { loop: true, volume: 0.1 });
        await this.sleep(250);
        if (!this.state.showQuestion) {
            //await audio.playVo(questionType === QuestionTypes.SINGLE ? VO.NextQuestionSingle : VO.NextQuestionGroup);
            //await this.sleep(500);
            audio.playSfx(SFX.DisplaySweep, {});
            this.setState({ showQuestion: true, });
            await this.sleep(750);
        }
        await audio.playVo(this.getPodiumsTurnVO());
        await this.sleep(500);
        this.sendIfPrimary(MESSAGES.TO_SERVER.BEGIN_ANSWERING, {});
    }

    // Capitalize the first letter so a matched (canonical) answer and an
    // unmatched user submission look identical during the countdown. Without
    // this, a lowercase-typed answer that got matched shows capitalized while
    // an incorrect one stays lowercase, spoiling the result before the reveal.
    capitalizeFirst(text) {
        if (!text) return text;
        return text.charAt(0).toUpperCase() + text.slice(1);
    }

    getAnswerCommentVO(score) {
        const questions = this.state.roomState?.pointlessData?.questions ?? [];
        const answers = [];
        questions.forEach(q => {
            if (q?.answers) answers.push(...q.answers);
        });

        // Extract unique scores, sorted ascending (lower = better)
        const uniqueScores = [...new Set(
            answers.map(a => a.points).filter(p => typeof p === "number")
        )].sort((a, b) => a - b);

        // Edge case: no or single unique score - use absolute thresholds
        if (uniqueScores.length <= 1) {
            if (score < 20) return VO.AmazingAnswer;
            if (score < 45) return VO.OkAnswer;
            return VO.NotGreatAnswer;
        }

        // Find rank (0 = best). If score not in list, find insertion point
        let rank = uniqueScores.indexOf(score);
        if (rank === -1) {
            rank = uniqueScores.filter(s => s < score).length;
        }

        // Calculate percentiles (0 = best, 1 = worst)
        const absolutePercentile = score / 100;
        const relativePercentile = rank / (uniqueScores.length - 1);

        // Blend: 60% absolute (score quality), 40% relative (ranking context)
        const blendedScore = (absolutePercentile * 0.6) + (relativePercentile * 0.4);

        // Apply tier thresholds
        if (blendedScore < 0.25) return VO.AmazingAnswer;
        if (blendedScore < 0.55) return VO.OkAnswer;
        return VO.NotGreatAnswer;
    }

    getPointlessVideo() {
        if (this.state.gameState === GAME_STATES.FINAL_ROUND_REVEAL) {
            return VIDEOS.POINTLESS_ANSWER_FR;
        } else {
            switch (this.state.roomState?.pointlessData?.roundNumber) {
                case 1:
                    return VIDEOS.POINTLESS_ANSWER_R1;
                case 2:
                    return VIDEOS.POINTLESS_ANSWER_R2;
                case 3:
                    return VIDEOS.POINTLESS_ANSWER_R3;
                case 4:
                    return VIDEOS.POINTLESS_ANSWER_R4;
                default:
                    return VIDEOS.POINTLESS_ANSWER_R1;
            }
        }
    }

    // Wrong-answer reveal clips. These open on a neutral tower and only turn red
    // partway through, so nothing on screen must telegraph the loss before then.
    // A visible Skip button is such a tell (a video only ever plays on a wrong
    // answer), so it's suppressed for these — see showSkipButton on VideoPlayer.
    get isSpoilerVideo() {
        return [
            VIDEOS.WRONG_ANSWER_R1_R2,
            VIDEOS.WRONG_ANSWER_R1_R2_LQ,
            VIDEOS.WRONG_ANSWER_R3,
            VIDEOS.WRONG_ANSWER_R3_LQ,
            VIDEOS.WRONG_ANSWER_R4,
            VIDEOS.WRONG_ANSWER_R4_LQ,
            VIDEOS.FINAL_WRONG_ANSWER,
            VIDEOS.FINAL_WRONG_ANSWER_END_GAME,
            VIDEOS.FINAL_NOT_POINTLESS_GAME_OVER,
        ].includes(this.state.videoSrc);
    }

    getWrongAnswerVideo(add = 0) {
        switch (this.state.roomState?.pointlessData?.roundNumber + add) {
            case 1:
            case 2:
                if (this.state.useMask) return VIDEOS.WRONG_ANSWER_R1_R2;
                else return VIDEOS.WRONG_ANSWER_R1_R2_LQ;
            case 3:
                if (this.state.useMask) return VIDEOS.WRONG_ANSWER_R3;
                else return VIDEOS.WRONG_ANSWER_R3_LQ;
            case 4:
                if (this.state.useMask) return VIDEOS.WRONG_ANSWER_R4;
                else return VIDEOS.WRONG_ANSWER_R4_LQ;
            default:
                if (this.state.useMask) return VIDEOS.WRONG_ANSWER_R1_R2;
                else return VIDEOS.WRONG_ANSWER_R1_R2_LQ;
        }
    }

    async doCountdownFlow() {
        await this.sleep(250);
        if ([GAME_STATES.FINAL_ROUND_REVEAL, GAME_STATES.FINAL_ROUND, GAME_STATES.FINAL_ROUND_SELECT].includes(this.state.gameState)) {
            this.setState({ wrongAnswer: false, countdownValue: 100, countdownFinished: false, activeStillBG: IMAGES.HOST.FinalRoundBase });
        } else {
            this.setState({ wrongAnswer: false, countdownValue: 100, countdownFinished: false, showCategory: false, showRoundNumber: false, });
        }

        //return; // DEBUG


        audio.stopAllMusic();
        await this.sleep(500);

        const pointlessData = this.state.roomState.pointlessData;
        //const activeTeam = pointlessData.teams[pointlessData.activeTeamIndex];
        const correctAnswer = pointlessData.teamCorrect;
        const team = pointlessData.teams[pointlessData.activeTeamIndex];
        console.log("PointlessData teamCorrect : ", pointlessData.teamCorrect);
        console.log("Correct Answer : ", correctAnswer);

        let question = pointlessData.questions.find(x => x.id === team.answeredQuestionId);
        let actualAnswer;
        if (correctAnswer) {
            actualAnswer = question.answers.find(x => x.id === team.answerId);
            if (actualAnswer.points === 0) {
                let nextState;
                if (this.state.gameState === GAME_STATES.REVEAL_ANSWER) nextState = GAME_STATES.ANSWER_IDLE;
                else nextState = GAME_STATES.GAME_OVER_IDLE;
                //this.setState({ showQuestion: false })
                this.doVideoLoad(this.getPointlessVideo(), this.getQuestionBG(), nextState, "", "pointless");
            }
        } else if (this.state.gameState === GAME_STATES.FINAL_ROUND_REVEAL) {
            //if (pointlessData?.finalAnswersLeft === 0) {
            //    this.doVideoLoad(VIDEOS.FINAL_WRONG_ANSWER_END_GAME, IMAGES.HOST.GameOverLostBG, GAME_STATES.FINAL_ANSWER_IDLE, "", "transition");
            //} else {
            this.doVideoLoad(VIDEOS.FINAL_WRONG_ANSWER, IMAGES.HOST.FinalRoundBase, GAME_STATES.FINAL_ANSWER_IDLE, "", "transition");
            //}
        } else {
            this.setState({ activeStillBG: this.getContestantCornerBG() })
            let nextState;
            if (this.state.gameState === GAME_STATES.REVEAL_ANSWER) nextState = GAME_STATES.ANSWER_IDLE;
            else nextState = GAME_STATES.FINAL_ANSWER_IDLE;
            this.doVideoLoad(this.getWrongAnswerVideo(), this.getQuestionBG(), nextState, "", "transition");
        }

        await audio.playVo(VO.SeeIfRight, {});

        if (!correctAnswer) {
            console.log("Team got a wrong answer!!!!");
            // big red ex sequence
            this.setState({ wrongAnswer: true });
            this.sendIfPrimary(MESSAGES.TO_SERVER.UPDATE_DROP_SCORE, {});
            this.playLoadedVideo();
            return;
            //await audio.playSfx(SFX.AnswerWrong);
            //await this.sleep(1000);
            //await audio.playVo(VO.WrongAnswer);
            //await this.sleep(1000);
            //this.state.room?.send(MESSAGES.TO_SERVER.COMPLETE_DROP, {});
        } else {
            await this.sleep(2000);
            console.log("team got it correct : ", correctAnswer);
            if (actualAnswer.points <= 80) {
                audio.playVo(VO.ItsRight, {});
            }
            let count = 0;
            let countdownSFX = this.state.gameState === GAME_STATES.FINAL_ROUND_REVEAL ? SFX.FinalRoundCounter : SFX.RoundsCounterSoftEnd;
            let countdownAudio = audio.playSfx(countdownSFX, {});

            for (let i = 100; i >= actualAnswer.points; i--) {
                if (this.state.gameState === GAME_STATES.FINAL_ROUND_REVEAL) await this.sleep(count * (i < 20 ? i < 10 ? 3 : 2 : 2));
                else await this.sleep(44);
                count++;
                this.setState({ countdownValue: i });
                if (i === actualAnswer.points) {
                    countdownAudio.handle.stop();
                    this.sendIfPrimary(MESSAGES.TO_SERVER.UPDATE_DROP_SCORE, {});
                    if (i === 0) {
                        //audio.playSfx(SFX.PointlessSting)
                        this.setState({  showCategory: false, showRoundNumber: false, countdownFinished: true, showQuestion: false, showDetailedQuestion: false, })
                        this.playLoadedVideo();
                        return;
                    } else
                        audio.playSfx(SFX.CounterStop);
                }
            }

            await this.sleep(53);
            this.setState({ countdownFinished: true });

            if (count < 100) {
                await this.sleep(4000);
                await audio.playVo(this.getAnswerCommentVO(actualAnswer.points));
            }

            await this.sleep(2000);

            this.sendIfPrimary(MESSAGES.TO_SERVER.COMPLETE_DROP, {});
        }
    }

    async doRoundSummary() {
        this.setState({ showRemaining: false, showCategory: false, showDetailedQuestion: false, hideRemaining: false, showRoundNumber: false, stillBG: this.getQuestionBG() });
        await audio.playVo(VO.PossibleAnswers);
        await this.sleep(250);
        audio.playSfx(SFX.DisplaySweep, {});
        this.setState({ showRemaining: true });
        if (this.state.roomState?.pointlessData?.questionType === QuestionTypes.GROUP) this.setState({ showQuestion: true });

        await this.sleep(1000);
        this.sendIfPrimary(MESSAGES.TO_SERVER.COMPLETE_SUMMARY, {});
        //    await this.sleep(8000);
    }

    async doCompleteSummary() {
        audio.playSfx(SFX.DisplaySweep, {});
        this.setState({ hideRemaining: true, showQuestion: false, showCategory: false, showRoundNumber: false, });
        await this.sleep(1500);
        //this.state.room?.send(MESSAGES.TO_SERVER.COMPLETE_SUMMARY, {});
    }

    async doFinalRoundIntro() {
        this.setState({ showQuestion: false, countdownValue: 100, wrongAnswer: false, countdownFinished: false, activeStillBG: this.getFinalOrderBG(), highlightCategory: false });
        await audio.playVo(VO.Final.Intro);
        // Trophy reveal follows straight off the back of the VO — no dead-air pause.
        this.sendIfPrimary(MESSAGES.TO_SERVER.MOVE_ON_FINAL, {});
    }

    async doFinalRoundStart() {
        audio.playMusic(MUSIC.TensionBed, { loop: true, volume: 0.1 });

        //await audio.playVo(VO.NextCategory);
        await this.sleep(500);
        audio.playSfx(SFX.DisplaySweep, {});
        this.setState({ showCategory: true });
        await this.sleep(500);
        await audio.playVo(this.getFinalPodiumCategoryVO());
        await this.sleep(250);
        this.sendIfPrimary(MESSAGES.TO_SERVER.BEGIN_FINAL_CATEGORY_SELECT, {});

    }

    getFinalPodiumCategoryVO() {
        return VO.FinalCategory[this.state.roomState?.pointlessData.activeTeamIndex];
    }

    async doFinalRoundQuestionIntro() {
        this.setState({ highlightCategory: true });
        await this.sleep(250);
        await audio.playVo(VO.CategoryComment, {});
        await this.sleep(2000);
        audio.playSfx(SFX.DisplaySweep, {});
        this.setState({ showQuestion: true });
        await audio.playVo(VO.Final.Started);
        await this.sleep(500);
        await audio.playVo(VO.Final.GiveMeAnswers);
        this.sendIfPrimary(MESSAGES.TO_SERVER.BEGIN_FINAL_ANSWERING, {});
    }

    async doFinalQuestionDisplay() {
        if (this.state.showQuestion) return;
        audio.sequence([
            sfx(SFX.DisplaySweep, {}),
            wait(250),
            vo(this.getPodiumsTurnVO()),
        ]);
        this.setState({ showQuestion: true });
    }

    async doGameOver() {
        audio.playMusic(MUSIC.PreCreditsTheme, { volume: 0.1, loop: true });
        if (this.state.roomState?.pointlessData?.finalRoundWin) {
            //audio.playVo(VO.Final.YouWin);
            this.setState({ activeStillBG: IMAGES.HOST.GameOverTrohpyBG });
        } else {
            //audio.playVo(VO.Final.YouLose);
            this.setState({ activeStillBG: IMAGES.HOST.GameOverLostBG });
        }

        this.sendIfPrimary(MESSAGES.TO_SERVER.END_GAME, {});
    }

    coerceType(nextValue, currentValue) {
        if (typeof currentValue === "number") {
            const n = Number(nextValue);
            return Number.isNaN(n) ? currentValue : n;
        }
        return nextValue;
    }

    debugChangeState = (e) => {
        const next = this.coerceType(e.target.value, this.state.gameState);
        console.log(`next game state : ${next}`);
        this.doGameStateUpdate(next);
    }

    getQuestionBG() {
        if (this.state.gameState === GAME_STATES.FINAL_ROUND_REVEAL) {
            return IMAGES.HOST.GameOverTrohpyBG;
        } else {
            switch (this.state.roomState?.pointlessData?.roundNumber) {
                case 1:
                case 2:
                    return IMAGES.HOST.R1R2BG;
                case 3:
                    return IMAGES.HOST.R3BG;
                case 4:
                    return IMAGES.HOST.R4BG;
                default:
                    return IMAGES.HOST.R1R2BG;
            }
        }
    }

    getRoundIntroVideo(add = 0) {
        console.log("ROUD NUMBER : ", this.state.roomState?.pointlessData?.roundNumber + add)
        switch (this.state.roomState?.pointlessData?.roundNumber + add) {
            case 0:
                return VIDEOS.TRANSITION_MENU_R1;
            case 1:
                return VIDEOS.TRANSITION_MENU_R1;
            case 2:
                return VIDEOS.TRANSITION_R1_R2;
            case 3:
                return VIDEOS.TRANSITION_R2_R3;
            case 4:
                return VIDEOS.TRANSITION_R3_R4;
            default:
                return VIDEOS.TRANSITION_R1_R2;
        }
    }

    getFinalAnswerVO() {
        switch (this.state.roomState?.pointlessData?.finalAnswersLeft) {
            case 3:
                return VO.Final.FirstAnswer;
            case 2:
                return VO.Final.SecondAnswer;
            case 1:
                return VO.Final.ThirdAnswer;
            default:
                return VO.Final.ThirdAnswer;
        }
    }

    getFinalTransitionVideo() {
        let finalTeams = this.state.roomState?.pointlessData?.finalRoundTeamOrder;
        switch (finalTeams.length) {
            case 1:
                return VIDEOS.FINAL_TRANSITION_1;
            case 2:
                return VIDEOS.FINAL_TRANSITION_2;
            case 3:
                return VIDEOS.FINAL_TRANSITION_3;
            case 4:
                return VIDEOS.FINAL_TRANSITION_4;
            default:
                return VIDEOS.FINAL_TRANSITION_4;

        }
    }

    async doGameStateUpdate(gameState) {
        console.log("Do GameState Update : ", gameState);
        switch (gameState) {
            case GAME_STATES.LOADING:
                //if (useTestState) {
                //    this.setState({ gameState });
                //    this.doWelcome();
                //} else
                //this.doVideoFlow(IntroVideo, IntroBG, gameState, `${styles.fade} ${styles.blur}`);
                this.setState({ gameOver: false, })
                this.doVideoFlow(VIDEOS.INTRO, IMAGES.HOST.IntroBG, gameState);
                break;
            case GAME_STATES.MENU:
                this.setState({ gameState, activeStillBG: IMAGES.HOST.IntroBG });
                this.doWelcome();
                break;
            case GAME_STATES.ROUND_INTRO:
                if (this.state.roomState?.pointlessData?.roundNumber > 1) await this.doCompleteSummary();
                this.loadVideo(this.getWrongAnswerVideo(1).src);

                if (optimised) {
                    this.setState({ gameState, activeStillBG: this.getQuestionBG(), })
                    await this.sleep(1000);
                    this.doIntroFlow();
                } else {
                    this.loadVideo(this.getRoundIntroVideo(1).src)

                    this.doVideoFlow(this.getRoundIntroVideo(), this.getQuestionBG(), gameState);
                }
                break;
            case GAME_STATES.CATEGORY_DISPLAY:
                this.setState({ gameState, activeStillBG: this.getQuestionBG(), })
                this.doCategoryFlow();
                break;
            case GAME_STATES.DETAILED_DISPLAY:
                this.setState({ gameState, activeStillBG: this.getQuestionBG() })
                if (!this.state.showDetailedQuestion) { 
                    audio.playSfx(SFX.DisplaySweep, {});
                    this.setState({ showDetailedQuestion: true,  });
            }
                break;
            case GAME_STATES.QUESTION_DISPLAY:
                this.setState({ gameState, activeStillBG: this.getQuestionBG(), })
                this.doQuestionOnlyFlow();
                break;
            case GAME_STATES.ANSWERING:
                this.setState({ showQuestion: true, gameState, activeStillBG: this.getQuestionBG() });
                break;
            case GAME_STATES.REVEAL_ANSWER:
                this.setState({ gameState })
                this.doCountdownFlow();
                break;
            case GAME_STATES.ROUND_SUMMARY:
                this.setState({ gameState, activeStillBG: this.getQuestionBG() });
                this.doRoundSummary();
                break;
            case GAME_STATES.FINAL_ROUND_INTRO:
                //this.setState({ gameState })
                //this.doFinalRoundIntro();
                await this.doCompleteSummary();
                {
                    const finalTeamCount = this.state.roomState?.pointlessData?.finalRoundTeamOrder?.length;
                    const finalTransitionEvents = this.buildVideoEvents(`FINAL_TRANSITION_${finalTeamCount}`);
                    this.doVideoFlow(this.getFinalTransitionVideo(), this.getFinalOrderBG(), gameState, "", "transition", finalTransitionEvents);
                }
                break;
            case GAME_STATES.FINAL_ROUND:
                this.setState({ showRemaining: false, hideRemaining: false, });
                this.doVideoFlow(VIDEOS.TROPHY, IMAGES.HOST.FinalRoundBase, gameState, "", "transition", this.buildVideoEvents("TROPHY"))
                break;
            case GAME_STATES.FINAL_ROUND_CATEGORY_SELECT:
                this.setState({ gameState })
                if (!this.state.showCategory) {
                    audio.playSfx(SFX.DisplaySweep, {});
                    this.setState({ showCategory: true });
                }
                break;
            case GAME_STATES.FINAL_ROUND_QUESTION_DISPLAY:
                this.setState({ gameState });
                this.doFinalRoundQuestionIntro();
                break;
            case GAME_STATES.FINAL_ROUND_ANSWERING:
                this.setState({ gameState })
                this.doFinalQuestionDisplay();
                break;
            case GAME_STATES.FINAL_ROUND_SELECT:
                this.setState({ gameState, countdownValue: 100, countdownFinished: false, wrongAnswer: false, showQuestion: true, });
                let sequence = [];
                if (!this.state.finalRoundStarted) {
                    this.setState({ finalRoundStarted: true });
                    sequence.push(vo(VO.Final.Intro1));
                    sequence.push(wait(500));
                    sequence.push(vo(VO.Final.Intro2));
                }
                sequence.push(wait(1000));
                sequence.push(vo(this.getFinalAnswerVO()));
                audio.sequence(sequence);
                break;
            case GAME_STATES.FINAL_ROUND_REVEAL:
                this.setState({ gameState })
                this.doCountdownFlow();
                break;
            case GAME_STATES.FINAL_ROUND_SUMMARY:
                this.setState({ gameState })
                this.doRoundSummary();
                break;
            case GAME_STATES.GAME_OVER:
                this.setState({ gameOver: true, });
                if (!this.state.roomState?.pointlessData?.finalRoundWin) {
                    this.doVideoFlow(VIDEOS.FINAL_NOT_POINTLESS_GAME_OVER, IMAGES.HOST.GameOverLostBG, gameState);
                } else {
                    this.setState({ gameState })
                    this.doGameOver();
                }
                break;
            case GAME_STATES.END_GAME:
                this.setState({ gameState })
                //this.doGameOver();
                break;
            default:
                break;
        }
    }

    handleVideoError(errorDetails) {
        console.error('Video failed to load:', errorDetails);
        // Optionally skip to next state or handle error
        // For example, you could automatically advance past the video:
        // this.handleVideoEnd();
    }

    doVideoLoad(videoSrc, stillSrc, nextGameState, nextStillStyles = "", videoType = "transition", videoEvents = []) {
        //const cacheBuster = `guid=${crypto.randomUUID()}`;
        //const newSrc = `${videoSrc}?${cacheBuster}`;

        this.setState({ nextGameState, videoSrc, nextStill: stillSrc, nextStillStyles, videoType, videoEvents, revealPodiumsOnVideo: false, fadeCountdownTower: false, shouldPlayVideo: false, });
    }

    // Resolve the timed events declared for a video key into { time, handler }
    // pairs the VideoPlayer can fire. Handlers switch on the declared event type.
    buildVideoEvents(videoKey) {
        const configs = VIDEO_EVENTS[videoKey];
        if (!configs) return [];
        return configs.map(cfg => ({
            time: cfg.time,
            handler: () => {
                switch (cfg.type) {
                    case "REVEAL_FINAL_PODIUMS":
                        this.setState({ revealPodiumsOnVideo: true });
                        break;
                    case "FADE_COUNTDOWN_TOWER":
                        this.setState({ fadeCountdownTower: true });
                        break;
                    default:
                        break;
                }
            },
        }));
    }

    playLoadedVideo() {
        console.log("START PLAYING LOADED VID");
        this.setState({ shouldPlayVideo: true, playingVideo: true, });
        if (this.state.videoType === "pointless") {
            this.setState({ hideVideoOverlays: true, });
        }
    }

    doVideoFlow(videoSrc, stillSrc, nextGameState, nextStillStyles = "", videoType = "transition", videoEvents = []) {
        //const cacheBuster = `guid=${crypto.randomUUID()}`;
        //const newSrc = `${videoSrc}?${cacheBuster}`;

        audio.stopAllAudio();
        this.setState({ nextGameState, videoSrc, nextStill: stillSrc, nextStillStyles, videoType, videoEvents, revealPodiumsOnVideo: false, fadeCountdownTower: false, shouldPlayVideo: true, playingVideo: true, });
    }

    onVideoPlay = () => {
        console.log("***** VIDEO HAS ACTUALLY STARTED *****");
        this.setState({ playingVideo: true, });
    }

    onUpdateStill = () => {
        console.log("Video started, updating still");
        this.setState({ activeStillBG: this.state.nextStill, activeStillStyles: this.state.nextStillStyles, });
    }

    async doDropVideoFinished(skipVO = false) {
        if (!skipVO) {
            if (!this.state.wrongAnswer) {
                await audio.playVo(VO.ItsPointless);
            } else {
                await audio.playVo(VO.WrongAnswer);
            }
        }

        this.sendIfPrimary(MESSAGES.TO_SERVER.COMPLETE_DROP, {});
    }

    onVideoEnded = () => {
        console.log("Video Ended");
        console.log(`Video Type : ${this.state.videoType}`);

        // Note: revealPodiumsOnVideo is intentionally NOT reset here — the podium
        // overlay must stay revealed as the FINAL_TRANSITION video hands off to
        // the static FINAL_ROUND_INTRO view. The next video flow (doVideoFlow /
        // doVideoLoad, e.g. the trophy) clears it.
        this.setState({ gameState: this.state.nextGameState, playingVideo: false, hideVideoOverlays: false, videoSrc: null, videoEvents: [] });

        switch (this.state.nextGameState) {
            case GAME_STATES.LOADING:
                this.sendIfPrimary(MESSAGES.TO_SERVER.LOADING_COMPLETE, {});
                break;
            case GAME_STATES.ROUND_INTRO:
                this.doIntroFlow();
                break;
            case GAME_STATES.ANSWER_IDLE:
                this.doDropVideoFinished();
                break;
            case GAME_STATES.FINAL_ROUND_INTRO:
                this.doFinalRoundIntro();
                break;
            case GAME_STATES.FINAL_ROUND:
                this.doFinalRoundStart();
                break;
            case GAME_STATES.FINAL_ROUND_SELECT:
                this.setState({ countdownValue: 100, countdownFinished: false, wrongAnswer: false });
                audio.playVo(this.getFinalAnswerVO());
                break;
            case GAME_STATES.FINAL_ANSWER_IDLE:
                this.doDropVideoFinished();
                break;
            case GAME_STATES.GAME_OVER_IDLE:
                this.doDropVideoFinished(true);
                break;
            case GAME_STATES.GAME_OVER:
                this.doGameOver();
                break;
            default:
                break;
        }
    }

    // Reconnect flow:
    // 1. Reconnect using token from URL
    // 2. If sessionId matches roomState.host.id → primary host, proceed normally
    // 3. Otherwise → use ?mirror=true URL hint while awaiting server confirmation
    // 4. Server responds via "mirror_status" to confirm role
    doReconnect = () => {
        const token = this.getQueryStringValue("token");
        const mirrorHint = this.getQueryStringValue("mirror") === "true"; // URL fallback hint
        console.log("Token:", token, "mirrorHint:", mirrorHint);

        if (this.state.reconnectTries < 5 && token) {
            console.log("Connecting to room:", token)

            if (this.state.connected === false) {
                this.client.reconnect(token).then(room => {
                    console.log(room.sessionId, "joined", room.name);
                    this.setState({ room: room, roomId: room.roomId, myId: room.sessionId, connected: true, reconnectionToken: room.reconnectionToken });

                    const $ = Colyseus.getStateCallbacks(room);

                    room.onStateChange.once((roomState) => {
                        console.log("this is the first room state!", roomState);
                        this.setState({ roomState: roomState, });

                        // Initialize seeded random for deterministic audio (mirror sync)
                        const seed = roomState.pointlessData?.randomSeed;
                        if (seed && !this._seededRandomsInitialized) {
                            this._seededRandomsInitialized = true;
                            const rngs = createSeededRandoms(seed);
                            audio.setSeededRandom(rngs.audio);
                        }

                        const isPrimaryHost = roomState.host.id === room.sessionId;

                        if (isPrimaryHost) {
                            // We are the primary host — proceed normally
                            this.setState({ isMirror: false });
                            this.updateToken(room.reconnectionToken);
                            room.send("update_host_token", { reconnectionToken: room.reconnectionToken });
                            room.send("host_joined_game", { gameId: GAME_ID });
                        } else {
                            // Not primary host — use URL hint while we confirm with server
                            this.setState({ isMirror: mirrorHint });
                            // Update URL with fresh token, preserving mirror flag for refresh
                            try {
                                window.history.replaceState(null, null, window.location.pathname + `?token=${room.reconnectionToken}&mirror=true`);
                            } catch (e) { console.warn(e); }
                            room.send("check_mirror_status", {});
                        }

                        $(room.state).pointlessData?.listen("gameState", (value) => {
                            console.log(`GameState updated : `, value);
                            this.doGameStateUpdate(value);
                        });

                        $(room.state).pointlessData?.listen("timer", (value) => {
                            if (value <= 10) {
                                audio.playSfx(SFX.Tok);
                            }
                        });
                    });

                    room.onStateChange((state) => {
                        console.log(room.name, "has new state:", state);
                        this.setState({ roomState: state, });
                    });

                    room.onMessage("mirror_status", (message) => {
                        console.log("mirror_status received:", message);
                        if (message.isMirror) {
                            this.setState({ isMirror: true });
                        } else if (!message.isPrimaryHost) {
                            // Not primary host and not a mirror — redirect away
                            console.log("Not primary host or mirror, redirecting");
                            window.location = this.getRedirectURL();
                        }
                    });

                    room.onMessage("animate_potato", (message) => {
                        console.log("animate_potato", "received on", room.name, message);
                        this.animatePotato(message.id, message.animation);
                    });

                    room.onMessage("game_over", (message) => {
                        console.log("game_over", "received on", room.name, message);
                    });

                    room.onMessage("change_game", (message) => {
                        console.log("change_game", "received on", room.name, message);
                        let platformPath = "/lobby";
                        if (this.state.roomState?.platform === APP_TYPES.ROKU) platformPath = "/roku";
                        else if (this.state.roomState?.platform === APP_TYPES.FIRE_TV) platformPath = "/firetv";
                        this._isReturningToLobby = true;
                        let redirectUrl;
                        if (this.state.isMirror) {
                            redirectUrl = `${this.getRedirectURL()}${platformPath}/?mirror=${this.state.roomId}&token=${this.state.reconnectionToken}`;
                        } else {
                            redirectUrl = `${this.getRedirectURL()}${platformPath}/?token=${this.state.reconnectionToken}`;
                        }
                        this.setState({ redirect: redirectUrl });
                        if (this.locationCheckInterval) clearInterval(this.locationCheckInterval);
                        // Need a NON-consented close so the server preserves the host session for
                        // reconnection from the lobby page (leave(true) would send LEAVE_ROOM and
                        // trigger host removal → "no host when client left" → room disposes).
                        // But leave(false) alone triggers the SDK's built-in auto-retry loop, which
                        // reconnects us into the now-lobby room and re-fires change_game. Disable the
                        // SDK retry first, then close the WS ourselves.
                        try {
                            if (this.state.room) {
                                this.state.room.reconnection.maxRetries = 0;
                                this.state.room.leave(false);
                            }
                        } catch { }
                    });

                    room.onMessage("new_game", (message) => {
                        console.log("new_game", "received on", room.name, message);

                    });

                    room.onError((code, message) => {
                        console.log(this.client.id, "couldn't join", room.name);
                        //LoggingService.logError(message, code);
                    });

                    room.onLeave((code) => {
                        console.log(this.client.id, "left", room.name, "code:", code);
                        if (this._isReturningToLobby) return;

                        // Session-ended codes (4200-4299): redirect immediately, don't reconnect
                        if (code >= 4200 && code < 4300) {
                            let platformPath = "/lobby";
                            if (this.state.roomState?.platform === APP_TYPES.ROKU) platformPath = "/roku";
                            else if (this.state.roomState?.platform === APP_TYPES.FIRE_TV) platformPath = "/firetv";
                            this.setState({ redirect: `${this.getRedirectURL()}${platformPath}/?code=${code}` });
                            if (this.locationCheckInterval) clearInterval(this.locationCheckInterval);
                            return;
                        }

                        if (!this.state.redirect) {
                            // Delayed, deduped retry (guarded by connected===false above)
                            // so we don't race the 0.17 SDK's own auto-retry. Back off
                            // per close code — matches OnePercentClub.
                            this.setState({ connected: false, reconnectTries: this.state.reconnectTries + 1 });
                            setTimeout(() => {
                                this.doReconnect();
                            }, retryDelayForCode(code));
                        }
                    });
                }).catch(e => {
                    console.log("JOIN/RECONNECT ERROR", e);
                    // A genuinely terminal reconnect error (room disposed / token expired)
                    // won't recover by retrying — go back to the lobby.
                    if (isTerminalReconnectError(e)) {
                        let platformPath = "/lobby";
                        if (this.state.roomState?.platform === APP_TYPES.ROKU) platformPath = "/roku";
                        else if (this.state.roomState?.platform === APP_TYPES.FIRE_TV) platformPath = "/firetv";
                        this.setState({ connected: false, redirect: `${this.getRedirectURL()}${platformPath}` });
                        if (this.locationCheckInterval) clearInterval(this.locationCheckInterval);
                        return;
                    }
                    this.setState({ connected: false, reconnectTries: this.state.reconnectTries + 1 });
                    setTimeout(() => {
                        this.doReconnect();
                    }, 2000);
                });
            }
        } else {
            if (mirrorHint) {
                console.log("Mirror host: reconnect exhausted, staying on page");
                return;
            }
            let platformPath = "/lobby";
            if (this.state.roomState?.platform === APP_TYPES.ROKU) platformPath = "/roku";
            else if (this.state.roomState?.platform === APP_TYPES.FIRE_TV) platformPath = "/firetv";
            this.setState({ redirect: `${this.getRedirectURL()}${platformPath}` });//Might need aditional search query url for roku incase the roomstate is null
            if (this.locationCheckInterval) clearInterval(this.locationCheckInterval);
        }
    }


    renderTextDiv(text, additionalStyles) {
        return <>
            <div className={`${styles.text} ${additionalStyles}`}>{text}
                <div className={styles.face}>{text} </div>
            </div>
        </>
    }

    renderMainButton(text, alt = false, disabled = false, callback = null, additionalStyles) {
        return (
            <button className={`${styles.mainButton} ${alt && styles.alt} ${additionalStyles}`} onClick={callback} tabIndex='-1' disabled={disabled}>
                <img src={IMAGES.HOST.ButtonGlow} className={styles.glow} />
                <img src={IMAGES.HOST.categoryCapsule} alt="btn-bg" />
                {this.renderTextDiv(text)}

            </button>
        )
    }

    renderMainMenu() {
        const showMainMenu = this.state.gameState === GAME_STATES.MENU && this.state.connected && !this.state.playingVideo;
        return (
            <div className={`${styles.startButtonSection} ${showMainMenu && styles.show}`}>
                {/*<img className={styles.menuLogo} src={logo } />*/}
                {this.renderMainButton("START GAME", false, false, this.signalStartGame, styles.hidden)}
                {/*{this.renderMainButton("RETURN TO LOBBY", true, false, this.goToLobby)}*/}
                <div className={styles.textBubble}>
                    <img src={IMAGES.HOST.categoryCapsule} />
                    {this.renderTextDiv("Select a podium using your device!", styles.small)}
                </div>
            </div>
        )
    }

    renderMenuTeams() {
        const teams = this.state.roomState?.pointlessData?.teams;
        const showMenuTeams = this.state.gameState === GAME_STATES.MENU && teams && !this.state.playingVideo;

        return (
            <div className={`${styles.menuTeams} ${showMenuTeams && styles.show}`}>
                {
                    teams.map((team) => {
                        return this.renderTeam(team, false, null, true)
                    })
                }
            </div>
        )
    }

    getTeamPodium(id) {
        switch (id) {
            case 0:
                return IMAGES.HOST.podium1;
            case 1:
                return IMAGES.HOST.podium2;
            case 2:
                return IMAGES.HOST.podium3;
            case 3:
                return IMAGES.HOST.podium4;
            default:
                return null;
        }
    }

    renderTeam(team, showActive = false, finalPosition = null, menu = false, finalRound = false) {
        if (!team) return;

        const active = team.active;
        const finalOrdinal = this.getOrdinal(finalPosition);

        const showNames = [GAME_STATES.MENU, GAME_STATES.GAME_OVER, GAME_STATES.END_GAME].includes(this.state.gameState);
        const teamPlayers = this.getTeamPlayers(team);

        return (
            <div className={`${styles.team} ${!active && styles.hide} ${showActive && styles.raise} ${menu && styles.menu}`} key={team.id} style={{ "--team-colour": team.colour }}>
                {/*{showActive && <div className={styles.glow} /> }*/}
                <div className={styles.podium} >
                    <img src={IMAGES.HOST.teamStand} className={styles.stand} />
                    {showActive && <img src={IMAGES.HOST.StandGlow} className={styles.stand} />}

                    <div className={styles.podiumName}>
                        <img src={this.getTeamPodium(team.id)} />
                        <div className={styles.names}>
                            {teamPlayers[0] && this.renderTextDiv(`${teamPlayers[0].name}`)}
                            {teamPlayers[1] && this.renderTextDiv(`${teamPlayers[1].name}`)}
                        </div>
                    </div>

                    {
                        showNames ?
                            this.renderStandNames(team, teamPlayers)
                            :
                            ([GAME_STATES.REVEAL_ANSWER, GAME_STATES.FINAL_ROUND_REVEAL].includes(this.state.gameState) || this.state.roomState?.pointlessData?.answerTesting) && !this.state.countdownFinished ?
                                <div key={`team-answer-${team.id}-${team.answer}`} className={`${styles.score} ${styles.small}`}>{this.capitalizeFirst(team.answer)}</div>
                                :
                                finalRound ?
                                    <div key={`team-score-${team.id}-${team.score}`} className={`${styles.score} ${styles.small}`}>FINAL ROUND</div>
                                    :
                                    <div key={`team-score-${team.id}-${team.score}`} className={styles.score}>{finalOrdinal || team.score}</div>
                    }
                </div>
                <div className={styles.players}>
                    <img data-avatar={teamPlayers[0]?.id} className={`${styles.player} ${!teamPlayers[0] && styles.hide}`} src={teamPlayers[0]?.avatar} />
                    <img data-avatar={teamPlayers[1]?.id} className={`${styles.player} ${!teamPlayers[1] && styles.hide}`} src={teamPlayers[1]?.avatar} />

                </div>
            </div>
        )
    }

    // One block line per name, centred as a group on the stand screen. Font
    // shrinks (inline) only when the widest name won't fit at the base size.
    renderStandNames(team, teamPlayers) {
        const names = teamPlayers.filter(Boolean).map((p) => p.name);
        const widestEm = Math.max(0, ...names.map(avantWidthEm));
        const fontCh = widestEm ? Math.min(STAND_NAME_FONT_CH, STAND_NAME_MAX_WIDTH_CH / widestEm) : STAND_NAME_FONT_CH;
        const style = fontCh < STAND_NAME_FONT_CH ? { fontSize: `calc(${fontCh.toFixed(3)} * var(--ch, 1vh))` } : undefined;

        return (
            <div key={`team-answer-${team.id}-${names[0]}`} className={`${styles.score} ${styles.small} ${styles.names}`} style={style}>
                {names.map((name, i) => <span key={i} className={styles.standName}>{name}</span>)}
            </div>
        );
    }

    getOrdinal(n) {
        if (!n) return null;

        const suffixes = ["th", "st", "nd", "rd"];
        const v = n % 100;
        return n + (suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0]);
    }

    getTeamPlayers(team) {
        if (!team) return [];

        const players = team.memberIds.map((id) => { return this.state.roomState.players.get(id) });
        return players;
    }

    getQuestionContainers(displayQuestion, questionOnly = false, frozen = null) {
        // `frozen` lets a caller pin the question data (questions + type) while
        // its pills are transitioning away, so the next round's text doesn't
        // repaint into the old pills mid-fade. When null, render live state.
        const questions = frozen?.questions ?? this.state.roomState.pointlessData?.questions;
        const questionType = frozen?.questionType ?? this.state.roomState.pointlessData?.questionType;

        switch (questionType) {

            // --- SINGLE ---
            case QuestionTypes.SINGLE: {
                const question = questions?.[0]?.question ?? "";
                const textSize = this.getTextSizeStyle(question, 20, 40);

                return (
                    <div className={`${styles.bubbleContainer} ${displayQuestion && styles.show}`}>
                        <div className={`${styles.bubble} ${styles.single}`}>
                            <img src={IMAGES.HOST.categoryCapsule} />
                            {this.renderTextDiv(question, textSize)}
                        </div>
                    </div>
                );
            }

            // --- GROUP ---
            case QuestionTypes.GROUP: {
                const questionArray = Array.from(questions || []);

                // collect all texts (questions + answered answers)
                const allQuestions = questionArray.map(q => q.question);

                const allAnswers = questionArray
                    .map(q => q.answers.find(a => a.answer))
                    .map(a => a.answer);

                // Find longest strings
                const longestAnswer = allAnswers.reduce((a, b) => a.length > b.length ? a : b, "");
                const longestQuestion = allQuestions.reduce((a, b) => a.length > b.length ? a : b, "");

                // Compute sizes
                let answerSize = this.getTextSizeStyle(longestAnswer, 8, 18);
                const questionSize = this.getTextSizeStyle(longestQuestion, 50, 90);

                console.log(`Answer Size : ${answerSize}`);
                console.log(`Question Size : ${questionSize}`);
                console.log(`matching : ${questionSize === answerSize}`);

                // Determine shared size priority: small → medium → large
                //const sizes = [answerSize, questionSize];
                if (questionSize === styles.small) {
                    answerSize = questionSize;
                } else if (questionSize === styles.medium) {
                    if (answerSize !== styles.small) answerSize = questionSize;
                }

                //let sharedSize =
                //    sizes.includes(styles.small) ? styles.small :
                //        sizes.includes(styles.medium) ? styles.medium :
                //            sizes.includes(styles.large) ? styles.large :
                //                undefined;

                return questionArray.map((q, i) => {
                    let answered;
                    if (questionOnly && !this.state.showRemaining) {
                        answered = q.answers.find(a => a.answered);
                    } else {
                        answered = q.answers.find(a => a.answered || a.reveal);
                    }
                    return (
                        <div key={i} className={`${styles.bubble} ${styles.group}`}>
                            <img src={IMAGES.HOST.capsule} />
                            {this.renderTextDiv(q.question, questionSize)}
                            {this.renderTextDiv(answered?.points, `${!answered && styles.hide}`)}
                            {this.renderTextDiv(
                                answered?.answer,
                                `${!answered && styles.hide} ${answerSize}`
                            )}
                        </div>
                    );
                });
            }

            // --- FINAL ---
            case QuestionTypes.FINAL: {
                // find the longest question among the 3
                const allQs = questions?.map(q => q?.question ?? "") ?? [];
                const longestQ = allQs.reduce((a, b) => (a.length > b.length ? a : b), "");
                const sharedSize = this.getTextSizeStyle(longestQ, 40, 80);

                return (
                    <div className={`${styles.bubbleContainer} ${displayQuestion && styles.show} ${(this.state.showRemaining || this.state.hideRemaining) && styles.hide} ${this.state.hideRemaining && styles.hide}`}>
                        {questions.slice(0, 3).map((q, i) => (
                            <div key={i} className={`${styles.bubble} ${styles.single}`}>
                                <img src={IMAGES.HOST.categoryCapsule} />
                                {this.renderTextDiv(q?.question, sharedSize)}
                            </div>
                        ))}
                    </div>
                );
            }

            default:
                return null;
        }
    }


    renderTimer(show = false) {
        const pointlessData = this.state.roomState?.pointlessData;

        let clockImg;
        if (pointlessData?.questionType === QuestionTypes.FINAL) {
            clockImg = IMAGES.HOST.ClockFR;
        } else {
            switch (pointlessData?.roundNumber) {
                case 1:
                    clockImg = IMAGES.HOST.ClockR1;
                    break;
                case 2:
                    clockImg = IMAGES.HOST.ClockR2;
                    break;
                case 3:
                    clockImg = IMAGES.HOST.ClockR3;
                    break;
                case 4:
                    clockImg = IMAGES.HOST.ClockR4;
                    break;
                default:
                    clockImg = IMAGES.HOST.ClockR1;
                    break;
            }
        }

        return (
            <div className={`${styles.timer} ${show && styles.show} ${pointlessData?.timerPaused && styles.paused}`}>
                <img src={clockImg} alt="clock-img" />
                <div className={`${styles.time} ${pointlessData?.timerRunning && styles.show}`}>
                    <img src={IMAGES.HOST.ClockCircle} alt="clock-circle-img" />
                    {this.renderTextDiv(this.formatTimerDisplay())}
                </div>
            </div>
        )
    }

    renderQuestionView() {
        const gameState = this.state.gameState;
        const isCategory = gameState === GAME_STATES.CATEGORY_DISPLAY;
        const pointlessData = this.state.roomState?.pointlessData;
        const isQuestion = [GAME_STATES.ROUND_INTRO, GAME_STATES.CATEGORY_DISPLAY, GAME_STATES.ANSWERING, GAME_STATES.QUESTION_DISPLAY, GAME_STATES.ANSWER_IDLE, GAME_STATES.ROUND_SUMMARY, GAME_STATES.DETAILED_DISPLAY].includes(gameState);
        const showQuestionView = (isQuestion || isCategory) && !this.state.playingVideo;

        const displayRoundNumber = this.state.showRoundNumber;
        let roundNumber = pointlessData?.roundNumber;
        if (roundNumber == 0) roundNumber = 1;

        const displayCategory = this.state.showCategory && isCategory;
        const category = pointlessData?.category;
        const questionType = this.state.roomState.pointlessData?.questionType;

        const displayDetailedQuestion = this.state.showDetailedQuestion;
        const detailedQuestion = pointlessData?.questionExplanation;

        const displayQuestion = this.state.showQuestion;

        // Keep a snapshot of the question data that's on-screen while the pills
        // are shown. When they start fading out (displayQuestion === false), the
        // server may have already swapped `questions`/`questionType` to the next
        // round — so we render the snapshot until the pills are fully gone,
        // preventing the incoming question's text from repainting into them.
        // Scoped to this view only; the final-round view keeps rendering live.
        if (displayQuestion) {
            this._questionViewSnapshot = {
                questions: pointlessData?.questions,
                questionType: pointlessData?.questionType,
            };
        }
        const frozenQuestion = displayQuestion ? null : this._questionViewSnapshot;
        const displayQuestionType = frozenQuestion?.questionType ?? questionType;

        const timerRunning = pointlessData?.timerRunning;

        //const question = this.state.roomState.pointlessData?.questions[0];

        const teams = pointlessData?.teams;
        const activeTeamIndex = pointlessData?.activeTeamIndex;

        const filteredTeams = teams.filter(x => x.active);

        return (
            <div className={`${styles.questionView} ${showQuestionView && styles.show}`}>
                <img src={this.state.activeStillBG} className={styles.bg} />
                <div className={styles.circleContainer}>
                    <div className={styles.squareContainer}>
                        <div className={`${styles.categoryContainer} ${(displayCategory || displayDetailedQuestion) && styles.hide}`}>
                            <div className={`${styles.category} ${displayRoundNumber && styles.show}`}>
                                <img src={IMAGES.HOST.categoryCapsule} />
                                {this.renderTextDiv(`ROUND ${roundNumber}`, styles.large)}
                            </div>
                        </div>


                        <div className={`${styles.categoryContainer} ${(displayDetailedQuestion || displayQuestion) && styles.hide}`}>
                            <div className={`${styles.category} ${displayCategory && styles.show}`}>
                                <img src={IMAGES.HOST.categoryCapsule} />
                                {this.renderTextDiv(category, this.getTextSizeStyle(category))}
                            </div>
                        </div>

                        <div className={`${styles.bubbleContainer} ${displayDetailedQuestion && styles.show} ${displayQuestion && styles.hide}`}>
                            <div className={`${styles.bubbleContainer} ${displayDetailedQuestion && styles.show}`}>
                                <div className={`${styles.bubble} ${styles.single} ${styles.detailed}`}>
                                    <img src={IMAGES.HOST.largeCapsule} />
                                    {this.renderTextDiv(detailedQuestion, this.getTextSizeStyle(detailedQuestion, 150, 300))}
                                </div>
                            </div>
                        </div>

                        <div className={`${styles.bubbleContainer} ${displayQuestion && styles.show} ${((this.state.showRemaining || this.state.hideRemaining) && displayQuestionType === QuestionTypes.SINGLE) && styles.hide} ${this.state.hideRemaining && displayQuestionType === QuestionTypes.GROUP && styles.hide} ${displayQuestionType === QuestionTypes.GROUP && styles.group}`}>
                            {this.getQuestionContainers(displayQuestion, true, frozenQuestion)}
                        </div>

                        {this.getRoundSummaryContainer()}
                    </div>
                </div>
                <div className={`${styles.cornerTeams} ${styles.left}`}>
                    {this.renderTeam(filteredTeams[0], filteredTeams[0]?.id === activeTeamIndex)}
                    {this.renderTeam(filteredTeams[1], filteredTeams[1]?.id === activeTeamIndex)}
                </div>
                <div className={`${styles.cornerTeams} ${styles.right}`}>
                    {this.renderTeam(filteredTeams[2], filteredTeams[2]?.id === activeTeamIndex)}
                    {this.renderTeam(filteredTeams[3], filteredTeams[3]?.id === activeTeamIndex)}
                </div>

                {this.renderTimer(timerRunning)}
            </div>
        )
    }

    formatTimerDisplay() {
        const totalSeconds = Math.ceil(this.state.roomState?.pointlessData?.timer);
        //return `${String(totalSeconds).padStart(2, '0')}`;
        return `${String(totalSeconds)}`;
    }

    getTextSizeStyle(text = "", largeBracket = 10, mediumBracket = 25) {
        return text.length <= largeBracket ? styles.large : text.length <= mediumBracket ? styles.medium : styles.small;
    }

    getRoundSummaryContainer() {
        const showRemaining = this.state.showRemaining;
        const hideRemaining = this.state.hideRemaining;
        const pointlessData = this.state.roomState?.pointlessData;

        const questionType = pointlessData?.questionType;
        const questions = pointlessData?.questions;
        let answers = [];

        if (questionType === QuestionTypes.SINGLE) {
            const question = questions[0];
            answers = question.answers.filter(x => x.reveal === true);
        } else if (questionType === QuestionTypes.FINAL) {
            questions.forEach(x => {
                let filteredAnswers = x.answers.filter(y => y.reveal === true);
                answers.push(...filteredAnswers);
            });
        }

        let sharedSize = styles.medium;

        if (questionType === QuestionTypes.SINGLE) {
            const allTexts = answers.reduce((acc, a) => {
                acc.push(a.answer);
                return acc;
            }, []);

            // find the longest string
            const longestText = allTexts.reduce(
                (a, b) => (a.length > b.length ? a : b),
                ""
            );
            sharedSize = this.getTextSizeStyle(longestText, 20, 40);
        }

        return (
            <div className={`${styles.bubbleContainer} ${showRemaining && styles.show} ${styles.summary} ${hideRemaining && styles.hide} ${questionType === QuestionTypes.GROUP && styles.group}`}>
                {
                    [QuestionTypes.SINGLE, QuestionTypes.FINAL].includes(questionType) ?
                        answers.map(x => {
                            return (
                                <div className={styles.singleAnswer}>
                                    <img src={IMAGES.HOST.answerCapsule} />
                                    <div className={styles.answer}>{this.renderTextDiv(x.answer, `${sharedSize}`)}</div>
                                    <div className={styles.points}>{this.renderTextDiv(x.points)}</div>
                                </div>
                            )
                        })
                        :
                        null
                        //this.getQuestionContainers(showRemaining)
                }
            </div>
        )
    }

    renderRoundSummary() {
        return;
        const showRoundSummary = this.state.gameState === GAME_STATES.ROUND_SUMMARY && !this.state.playingVideo;
        const showRemaining = this.state.showRemaining;
        const hideRemaining = this.state.hideRemaining;
        const pointlessData = this.state.roomState?.pointlessData;

        const questionType = pointlessData?.questionType;
        const questions = pointlessData?.questions;
        let answers = [];

        if (questionType === QuestionTypes.SINGLE) {
            const question = questions[0];
            answers = question.answers.filter(x => x.reveal === true);
        }

        const teams = pointlessData?.teams;
        const filteredTeams = teams.filter(x => x.active);


        let sharedSize = styles.medium;

        if (questionType === QuestionTypes.SINGLE) {
            const allTexts = answers.reduce((acc, a) => {
                acc.push(a.answer);
                return acc;
            }, []);

            // find the longest string
            const longestText = allTexts.reduce(
                (a, b) => (a.length > b.length ? a : b),
                ""
            );
            sharedSize = this.getTextSizeStyle(longestText, 20, 40);
        }

        return (
            <div className={`${styles.questionView} ${showRoundSummary && styles.show}`}>
                <img src={this.getQuestionBG()} className={styles.bg} />
                <div className={styles.circleContainer}>
                    <div className={styles.squareContainer}>
                        <div className={`${styles.bubbleContainer} ${showRemaining && styles.show} ${styles.summary} ${hideRemaining && styles.hide} ${questionType === QuestionTypes.GROUP && styles.group}`}>
                            {
                                questionType === QuestionTypes.SINGLE ?
                                    answers.map(x => {
                                        //const answerSize = this.getTextSizeStyle(x.answer);
                                        return (
                                            <div className={styles.singleAnswer}>
                                                <img src={IMAGES.HOST.answerCapsule} />
                                                <div className={styles.answer}>{this.renderTextDiv(x.answer, `${sharedSize}`)}</div>
                                                <div className={styles.points}>{this.renderTextDiv(x.points)}</div>
                                            </div>
                                        )
                                    })
                                    :
                                    this.getQuestionContainers(showRemaining)
                            }
                        </div>
                    </div>
                </div>
                <div className={`${styles.cornerTeams} ${styles.left}`}>
                    {this.renderTeam(filteredTeams[0])}
                    {this.renderTeam(filteredTeams[1])}
                </div>
                <div className={`${styles.cornerTeams} ${styles.right}`}>
                    {this.renderTeam(filteredTeams[2])}
                    {this.renderTeam(filteredTeams[3])}
                </div>
                {this.renderTimer(showRoundSummary)}
            </div>
        )
    }

    getContestantCornerBG() {
        //if (this.state.countdownValue === 0) {
        //    return PointlessContestantCorner;
        //} else {
        const teamCorrect = this.state.roomState?.pointlessData?.teamCorrect;
        switch (this.state.roomState?.pointlessData?.roundNumber) {
            case 1:
            case 2:
                if (!teamCorrect) {
                    if (this.state.useMask) return IMAGES.HOST.R1R2WrongColumnCorner
                    else return IMAGES.HOST.R1R2WrongColumnCornerLQ;
                }
                return IMAGES.HOST.R1R2ContestantCorner;
            case 3:
                if (!teamCorrect) {
                    if (this.state.useMask) return IMAGES.HOST.R3WrongColumnCorner
                    else return IMAGES.HOST.R3WrongColumnCornerLQ;
                }
                return IMAGES.HOST.R3ContestantCorner;
            case 4:
                if (!teamCorrect) {
                    if (this.state.useMask) return IMAGES.HOST.R4WrongColumnCorner
                    else return IMAGES.HOST.R4WrongColumnCornerLQ;
                }
                return IMAGES.HOST.R4ContestantCorner;
            default:
                if (!teamCorrect) {
                    if (this.state.useMask) return IMAGES.HOST.R1R2WrongColumnCorner
                    else return IMAGES.HOST.R1R2WrongColumnCornerLQ;
                }
                return IMAGES.HOST.R1R2ContestantCorner;
        }
        //}
    }

    getColumnCornerBG() {
        //if (this.state.countdownValue === 0) {
        //    return PointlessColumnCorner;
        //} else {
        switch (this.state.roomState.pointlessData.roundNumber) {
            case 1:
            case 2:
                return this.state.useMask ? IMAGES.HOST.R1R2ColumnCorner : IMAGES.HOST.R1R2ColumnCornerLQ;
            case 3:
                return this.state.useMask ? IMAGES.HOST.R3ColumnCorner : IMAGES.HOST.R3ColumnCornerLQ;
            case 4:
                return this.state.useMask ? IMAGES.HOST.R4ColumnCorner : IMAGES.HOST.R4ColumnCornerLQ;
            default:
                return this.state.useMask ? IMAGES.HOST.R1R2ColumnCorner : IMAGES.HOST.R1R2ColumnCornerLQ;
        }
        //}
    }

    getBlueMask() {
        let threshold = 1.2;

        let amount = this.state.countdownValue * threshold;
        amount = Math.max(0, Math.min(100, amount));

        // Define a fade size (how "soft" the gradient edge is)
        let fade = 20; // adjust for stronger/weaker feathering

        return `linear-gradient(
        to bottom,
        rgb(0, 0, 0) 0%,                          /* fully hidden at very top */
        rgb(0, 0, 0) ${100 - amount - fade}%,     /* still hidden */
        rgba(0, 0, 0, 0) ${100 - amount}%,        /* start fading */
        rgba(0, 0, 0, 0) 100%                     /* fully revealed below */
    )`;
    }

    getBlueOpacity() {
        // Flip logic: hidden at 100, fully revealed at 0
        let amount = ((100 - this.state.countdownValue) - 20);
        amount = amount / 100;
        return amount;
    }

    getBlueMaskHeight() {
        // Flip logic: hidden at 100, fully revealed at 0
        let threshold = 1.2;
        let amount = (100 - this.state.countdownValue) * threshold;
        amount = Math.max(0, Math.min(100, amount));
        return amount;
    }

    getBlueBG() {
        switch (this.state.roomState?.pointlessData?.roundNumber) {
            case 1:
            case 2:
                return IMAGES.HOST.R1R2BlueColumn;
            case 3:
                return IMAGES.HOST.R3BlueColumn;
            case 4:
                return IMAGES.HOST.R4BlueColumn;
            default:
                return IMAGES.HOST.R1R2BlueColumn;
        }
    }

    getWrongBG() {
        switch (this.state.roomState?.pointlessData?.roundNumber) {
            case 1:
            case 2:
                return IMAGES.HOST.R1R2WrongColumnCorner;
            case 3:
                return IMAGES.HOST.R3WrongColumnCorner;
            case 4:
                return IMAGES.HOST.R4WrongColumnCorner;
            default:
                return IMAGES.HOST.R1R2WrongColumnCorner;
        }
    }

    renderAnswerView() {
        const pointlessData = this.state.roomState?.pointlessData;
        const teams = pointlessData?.teams;
        const activeTeamIndex = pointlessData?.activeTeamIndex
        const activeTeam = teams[activeTeamIndex];

        const showAnswerSection = this.state.gameState === GAME_STATES.REVEAL_ANSWER;
        const playingVideo = this.state.playingVideo;
        const teamCorrect = pointlessData?.teamCorrect;

        //const blueHeight = this.getBlueMaskHeight();
        //const fade = 20; // feathering
        //const fadeStart = Math.max(0, blueHeight - fade);

        return (
            <div className={`${styles.answerSection} ${showAnswerSection && !this.state.hideVideoOverlays && styles.show}`}>
                <div className={styles.teamCorner}>
                    <img src={this.getContestantCornerBG()} alt="contestant-bg" className={styles.bg} style={{ display: `${playingVideo ? "none" : "block"}` }} />
                    <img src={IMAGES.HOST.WhiteoutContestantCorner} alt="contestant-bg-flash" className={`${styles.bg} ${styles.hide} ${this.state.countdownFinished && this.state.countdownValue > 0 && styles.flash}`} />
                    <img src={IMAGES.HOST.PointlessContestantCorner} alt="contestant-bg-pointless" className={`${styles.bg} ${styles.hide} ${this.state.countdownValue === 0 && styles.appear}`} />

                    <div className={styles.teamBox}>
                        {this.renderTeam(activeTeam)}
                    </div>
                </div>
                <div className={`${styles.countDownSection} ${(playingVideo || !teamCorrect) && styles.hide}`}>
                    <img src={this.getColumnCornerBG()} alt="column-bg" className={styles.bg} />

                    {
                        this.state.useMask &&
                            <img src={this.getBlueBG()} alt="mask-bg" className={styles.bg} style={{ maskImage: this.getBlueMask() }} />
                    }

                    <img src={IMAGES.HOST.WhiteoutColumnCorner} alt="column-bg-flash" className={`${styles.bg} ${styles.hide} ${this.state.countdownFinished && this.state.countdownValue > 0 && styles.flash}`} />
                    <img src={this.getWrongBG()} alt="column-bg-wrong" className={`${styles.bg} ${teamCorrect && styles.hide}`} />
                    <img src={IMAGES.HOST.PointlessColumnCorner} alt="column-bg-pointless" className={`${styles.bg} ${styles.hide} ${this.state.countdownValue === 0 && styles.appear}`} />

                    {
                        teamCorrect &&
                        <Countdown optimise={!this.state.useMask} wrongAnswer={this.state.wrongAnswer} countdownValue={showAnswerSection ? this.state.countdownValue : 100} countdownFinished={this.state.countdownFinished} roundNumber={this.state.roomState?.pointlessData?.roundNumber} type={"mainRound"} />
                    }
                </div>
                <img className={styles.divider} src={IMAGES.HOST.countdownDivider} alt="divider" />
            </div>
        )
    }

    getFinalOrderBG() {
        const pointlessData = this.state.roomState?.pointlessData;
        const teamsOrder = pointlessData?.finalRoundTeamOrder;

        switch (teamsOrder.length) {
            case 1:
                return IMAGES.HOST.FinalIntroBG1;
            case 2:
                return IMAGES.HOST.FinalIntroBG2;
            case 3:
                return IMAGES.HOST.FinalIntroBG3;
            case 4:
                return IMAGES.HOST.FinalIntroBG4;
            default:
                return IMAGES.HOST.FinalIntroBG4;
        }
    }

    // Final-order stage background for the static FINAL_ROUND_INTRO view. The
    // podium avatars + ranks are no longer drawn here — a single overlay
    // (renderFinalOrderPodiumsOverlay) provides them across both the video and
    // static phases so there is only one coordinate space to align.
    renderFinalOrderPodiums() {
        const showPodiums = this.state.gameState === GAME_STATES.FINAL_ROUND_INTRO && !this.state.playingVideo;
        const finalRoundTeamOrder = this.state.roomState.pointlessData.finalRoundTeamOrder;
        return (
            <div className={`${styles.finalPodiumsView} ${showPodiums && styles.show} ${styles["players" + finalRoundTeamOrder.length]}`}>
                {/* Destination background — snaps on instantly (the .bg class has no
                    opacity transition) so it's already painted the moment the final
                    transition video hides, instead of the still fading in from black. */}
                <img className={`${styles.bg} ${this.state.playingVideo && styles.hide}`} src={this.getFinalOrderBG()} alt="final-order-bg" />
            </div>
        )
    }

    // Single podium avatars + ranks overlay, mounted at .gameContainer level so
    // it spans both phases: layered over the FINAL_TRANSITION video during the
    // reveal, then staying in place for the static FINAL_ROUND_INTRO view — one
    // element, one coordinate space, no two trees to align. Revealed with a
    // white-flash (see .podiumFlashReveal) at the moment the video's own flash
    // peaks (5.75s), fired by the VideoPlayer event, and persists until the next
    // video flow (the trophy) resets revealPodiumsOnVideo.
    renderFinalOrderPodiumsOverlay() {
        const finalRoundTeamOrder = this.state.roomState?.pointlessData?.finalRoundTeamOrder;
        if (!finalRoundTeamOrder) return null;

        const teams = this.state.roomState.pointlessData?.teams;
        const reveal = this.state.revealPodiumsOnVideo;
        return (
            <div className={`${styles.finalPodiumsView} ${styles.overlay} ${styles["players" + finalRoundTeamOrder.length]}`}>
                {
                    finalRoundTeamOrder.map((x, i) => {
                        const team = teams[x];
                        const teamPlayers = this.getTeamPlayers(team);
                        return (
                            <div key={`overlay-podium-${team.id}`} className={`${styles.finalPodium}`}>
                                <div className={`${styles.podiumFlashReveal} ${reveal ? styles.reveal : ""}`}>
                                    <div className={styles.players}>
                                        {
                                            teamPlayers?.filter(p => p).map((player) => {
                                                return <img key={player.id} data-avatar={player.id} className={styles.player} src={player.avatar} alt="avatar" />
                                            })
                                        }
                                    </div>
                                    <div className={styles.podium} >
                                        <div key={`overlay-position-${team.id}-${team.score}`} className={styles.score}>{this.renderTextDiv(this.getOrdinal(i + 1))}</div>
                                    </div>
                                </div>
                            </div>
                        )
                    })
                }
            </div>
        )
    }

    renderFinalRoundView() {
        const showFinalRound = [GAME_STATES.FINAL_ROUND, GAME_STATES.FINAL_ROUND_ANSWERING, GAME_STATES.FINAL_ROUND_SELECT, GAME_STATES.FINAL_ROUND_REVEAL, GAME_STATES.FINAL_ANSWER_IDLE, GAME_STATES.FINAL_ROUND_CATEGORY_SELECT, GAME_STATES.FINAL_ROUND_QUESTION_DISPLAY, GAME_STATES.FINAL_ROUND_SUMMARY].includes(this.state.gameState) && !this.state.gameOver;

        const pointlessData = this.state.roomState?.pointlessData;
        const teams = pointlessData?.teams;
        const activeTeamIndex = pointlessData?.activeTeamIndex
        const activeTeam = teams[activeTeamIndex];
        const playingVideo = this.state.playingVideo;
        const teamCorrect = pointlessData?.teamCorrect;
        const hideVideoOverlays = this.state.hideVideoOverlays;
        const category = pointlessData?.category;
        // Set by the trophy video's 6.79s event — begins fading the countdown
        // tower in over the video's crossfade to the final-round static shot,
        // linking the video to the gameplay view. Only the trophy uses the
        // opacity fade; other final-round videos keep the instant display hide.
        const fadeCountdownTower = this.state.fadeCountdownTower;
        const isTrophyVideo = this.state.videoSrc === VIDEOS.TROPHY;

        const finalCategories = pointlessData?.finalCategories;
        const highlightCategory = this.state.highlightCategory;
        const selectedCategoryIndex = finalCategories?.indexOf(pointlessData?.category);

        return (
            <div className={`${styles.finalRoundView} ${(showFinalRound || fadeCountdownTower) && styles.show}`}>
                <img className={`${styles.bg} ${playingVideo && styles.hide}`} src={IMAGES.HOST.FinalRoundBase} />
                <img src={IMAGES.HOST.FinalRoundFlash} alt="final-bg-flash" className={`${styles.bg}  ${styles.hide} ${this.state.countdownFinished && this.state.countdownValue > 0 && styles.flash}`} />
                {/*<img src={IMAGES.HOST.FinalRoundPointless} alt="final-bg-pointless" className={`${styles.bg}  ${styles.hide} ${this.state.countdownValue === 0 && styles.appear}`} />*/}

                {
                    this.state.useMask &&
                    <img src={IMAGES.HOST.FinalRoundBlue} alt="final-mask-bg" className={`${styles.bg} ${playingVideo && styles.hide}`} style={{ maskImage: this.getBlueMask() }} />
                }
                <div className={`${styles.finalCountdownSection} ${this.state.wrongAnswer && styles.wrongAnswer} ${(playingVideo && !isTrophyVideo) && styles.hide} ${(playingVideo && isTrophyVideo && !fadeCountdownTower) && styles.videoHidden}`}>
                    <Countdown type={"final"} wrongAnswer={this.state.wrongAnswer} countdownValue={showFinalRound ? this.state.countdownValue : 100} countdownFinished={this.state.countdownFinished} roundNumber={pointlessData?.roundNumber} />
                </div>

                <div className={styles.finalQuestionSection}>
                    <div className={styles.circleContainer}>
                        <div className={styles.squareContainer}>
                            <div className={`${styles.categoryContainer} ${this.state.showQuestion && styles.hide} ${styles.final}`}>
                                <div className={`${styles.category} ${this.state.showCategory && styles.show} ${styles.first} ${this.state.showCategory && highlightCategory && styles.highlight} ${selectedCategoryIndex === 0 && styles.chosen}`}>
                                    <img src={IMAGES.HOST.categoryCapsule} />
                                    {this.renderTextDiv(finalCategories[0], styles.medium)}
                                </div>
                                <div className={`${styles.category} ${this.state.showCategory && styles.show} ${styles.second} ${this.state.showCategory && highlightCategory && styles.highlight} ${selectedCategoryIndex === 1 && styles.chosen}`}>
                                    <img src={IMAGES.HOST.categoryCapsule} />
                                    {this.renderTextDiv(finalCategories[1], styles.medium)}
                                </div>
                            </div>
                            {/*<div className={`${styles.bubbleContainer} ${this.state.showQuestion && styles.show}`}>*/}
                            {this.getQuestionContainers(this.state.showQuestion)}
                            {/*</div>*/}

                            {this.getRoundSummaryContainer()}
                        </div>
                    </div>
                </div>

                {this.renderTimer(showFinalRound && !hideVideoOverlays)}
            </div>
        )
    }


    renderFinalTeam() {
        let showFinalTeam = [
            GAME_STATES.FINAL_ROUND,
            GAME_STATES.FINAL_ANSWER_IDLE,
            GAME_STATES.FINAL_ROUND_CATEGORY_SELECT,
            GAME_STATES.FINAL_ROUND_ANSWERING,
            GAME_STATES.FINAL_ROUND_SELECT,
            GAME_STATES.FINAL_ROUND_REVEAL,
            GAME_STATES.GAME_OVER,
            GAME_STATES.END_GAME,
            GAME_STATES.FINAL_ANSWER_IDLE,
            GAME_STATES.GAME_OVER_IDLE
        ].includes(this.state.gameState);

        const pointlessData = this.state.roomState?.pointlessData;
        const teams = pointlessData?.teams;
        const activeTeamIndex = pointlessData?.activeTeamIndex
        const activeTeam = teams[activeTeamIndex];
        const finalAnswersLeft = pointlessData?.finalAnswersLeft;
        const finalRoundWin = pointlessData?.finalRoundWin;
        const teamCorrect = pointlessData?.teamCorrect;
        const hideVideoOverlays = this.state.hideVideoOverlays;

        if (finalAnswersLeft === 0 && finalRoundWin === false && this.state.gameOver) {
            showFinalTeam = false;
        }

        return (
            <div className={`${styles.finalRoundView} ${styles.show}`}>
                <div className={`${styles.finalActiveTeam} ${showFinalTeam && styles.show} ${showFinalTeam && this.state.countdownValue === 0 && styles.grow}`}>
                    {this.renderTeam(activeTeam,false, null, false, true)}
                </div>
            </div>
        )
    }

    renderGameOver() {
        const showGameOver = [GAME_STATES.END_GAME, GAME_STATES.GAME_OVER, GAME_STATES.GAME_OVER_IDLE].includes(this.state.gameState) && !this.state.playingVideo;
        const winningTeam = this.state.roomState?.pointlessData?.teams[this.state.roomState?.pointlessData?.activeTeamIndex];
        const teamWon = this.state.roomState.pointlessData.finalRoundWin;

        return (
            <div className={`${styles.gameOverSection} ${showGameOver && styles.show} ${teamWon && styles.trophyView}`}>
                <img src={IMAGES.SHARED.PointlessLogo} className={`${styles.logo} ${!teamWon && showGameOver && styles.show}`} />
                {this.isPrimary && (
                    <div className={styles.buttons}>
                        {this.renderMainButton("PLAY AGAIN", false, false, this.signalNewGame, styles.hidden)}
                        {this.renderMainButton("LOBBY", true, false, this.goToLobby, styles.hidden)}
                    </div>
                )}
            </div>
        )
    }

    //renderEndGame() {
    //    const showEndGame = this.state.gameState === GAME_STATES.END_GAME && this.state.connected && !this.state.playingVideo;
    //    return (
    //        <div className={`${styles.startButtonSection} ${showEndGame && styles.show}`}>
    //            <div className={styles.stillBG}>
    //                <img src={IMAGES.HOST.EmptyBG} />
    //                <div className={`${styles.overlay} ${styles.fade} ${styles.blur}`} />
    //            </div>
    //            <img className={styles.menuLogo} src={logo} />
    //            {this.renderMainButton("PLAY AGAIN", false, false, this.signalNewGame)}
    //            {this.renderMainButton("RETURN TO LOBBY", true, false, this.goToLobby)}
    //        </div>
    //    )
    //}

    render() {
        // commented out for dev (this block handles the redirecting away from the game) 
        if (this.state.redirect && !useTestState) {
            return (
                <React.Fragment>
                    <ConnectingOverlay show text="Sending you to the lobby!" large />
                    <div style={{ opacity: 0 }}>
                        <Route path="/" render={() => (window.location = this.state.redirect)} />
                    </div>
                </React.Fragment>
            )
        }
        const pointlessData = this.state.roomState.pointlessData;

        return (
            <div className={styles.gameWrapper}>
                <audio ref />
                <div id="gameContainer" className={styles.gameContainer}>
                    <ConnectingOverlay show={!this.state.connected || !pointlessData} text="Connecting" />
                    {
                        !this.state.connected || !pointlessData ?
                            null
                            :
                            <React.Fragment>
                                {/*<Menu room={this.state.room} toggleMute={this.toggleMute} toggleMenu={this.toggleMenu} menuOpen={this.state.menuOpen} muted={this.state.muted} />*/}


                                <div className={`${styles.stillBG} ${styles.main} ${this.state.playingVideo && styles.fade}`}>
                                    <img src={this.state.activeStillBG} />
                                    {/*<div className={`${styles.overlay} ${this.state.activeStillStyles}`} />*/}
                                </div>

                                <VideoPlayer
                                    ref={this.videoPlayerRef}
                                    className={styles.videoPlayer}
                                    show={this.state.playingVideo}
                                    video={this.state.videoSrc}
                                    showSkipButton={window.location.host.includes("local") && !this.isSpoilerVideo}
                                    onEnded={this.onVideoEnded}
                                    onError={this.handleVideoError}
                                    onPlay={this.onVideoPlay}
                                    onUpdateStill={this.onUpdateStill}
                                    forceMute={pointlessData?.mutedSfx}
                                    events={this.state.videoEvents}
                                />

                                {this.isPrimary && (
                                    <OptionsMenu room={this.state.room}
                                        toggleMenu={this.toggleMenu}
                                        menuOpen={this.state.menuOpen}
                                        isHost={true}
                                        mutedMusic={pointlessData?.mutedMusic}
                                        mutedSfx={pointlessData?.mutedSfx}
                                        gameState={this.state.gameState} />
                                )}

                                <RoomDetails
                                    url={this.getRedirectURL(false)}
                                    displayUrl={this.getRedirectURL(true)}
                                    mutedMusic={pointlessData?.mutedMusic}
                                    mutedSfx={pointlessData?.mutedSfx}
                                    roomId={this.state.roomId}
                                    onToggleMute={this.toggleMute}
                                    onToggleMenu={this.toggleMenu}
                                    hideButtons={[APP_TYPES.ROKU, APP_TYPES.FIRE_TV].includes(this.state.roomState?.platform)}
                                    isMirror={this.state.isMirror}
                                />

                                {this.renderMainMenu()}
                                {this.renderMenuTeams()}
                                {this.renderQuestionView()}
                                {this.renderAnswerView()}
                                {this.renderRoundSummary()}
                                {this.renderFinalOrderPodiums()}
                                {this.renderFinalOrderPodiumsOverlay()}
                                {this.renderFinalRoundView()}
                                {this.renderGameOver()}
                                {this.renderFinalTeam()}
                                {/*{this.renderEndGame()}*/}
                                {window.location.host.includes("local") && this.renderDebugButtons()}
                            </React.Fragment>
                    }
                </div>
                {showConsolePanel && <ConsoleOverlayPanel scale={tvOverlayScale()} />}
                {AUDIO_DEBUG && <AudioDebugOverlay />}
                {PERF_OVERLAY && <PerfOverlay />}
            </div>
        );
    }

    // #region Debug Functions
    renderDebugButtons() {
        return (
            <div style={{ position: "absolute", zIndex: 999, pointerEvents: 'initial', fontSize: "10px", background: "white" }}>
                <button onClick={() => this.setState({ wrongAnswer: !this.state.wrongAnswer })}>Toggle Wrong Answer</button>
                <button onClick={() => this.setState({ countdownFinished: !this.state.countdownFinished })}>Toggle Countdown Finished</button>
                <button onClick={() => this.setState({ showRemaining: !this.state.showRemaining })}>Toggle Show Remaining</button>
                <button onClick={() => this.setState({ hideRemaining: !this.state.hideRemaining })}>Toggle Hide Remaining</button>
                <button onClick={() => this.setState({ highlightCategory: !this.state.highlightCategory })}>Toggle Highlight Category</button>

                <label>
                    state:&nbsp;
                    <select
                        value={String(this.state.gameState)}
                        onChange={this.debugChangeState}
                        style={{ cursor: 'pointer' }}
                    >
                        {stateOptions.map(([label, value]) => (
                            <option key={label} value={String(value)}>
                                {label}
                            </option>
                        ))}
                    </select>
                </label>
                <div style={{ marginTop: '4px' }}>
                    <label>
                        slider: {this.state.countdownValue}
                        <input
                            type="range"
                            min="0"
                            max="100"
                            value={this.state.countdownValue}
                            onChange={(e) => this.setState({ countdownValue: Number(e.target.value) })}
                            style={{ cursor: 'pointer' }}
                        />
                    </label>
                </div>

            </div>
        )
    }
    // #endregion
}